import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Extended guard: supports categories and page slugs while keeping legacy roles
const ProtectedRoute = ({ element, allowedRoles = [], allowedCategories = [], allowedPages = [] }) => {
  const { currentUser, userRole, userCategory, accountStatus, rolesArray, allowedPages: userAllowedPages, loading } = useAuth();

  // Show loading state if auth is still initializing
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/auth/login" replace />;
  }

  // Admin bypass - Admins are never 'pending'
  const isAdmin = userCategory === 'admin' || rolesArray?.includes('admin') || userRole === 'admin';
  if (isAdmin) return element;

  // Email Verification Check (Strict Mode)
  // Determine if user authenticated with a provider that auto-verifies (like Google)
  // Google providerId is typically 'google.com'
  const isSocialAuth = currentUser.providerData.some(p => p.providerId === 'google.com');

  // If not verified and not social auth (redundant for google as it sets verified: true, but good safety)
  if (!currentUser.emailVerified && !isSocialAuth) {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // Account Status Check (New Gate)
  // If user is pending or suspended, they cannot access protected routes regardless of role
  if (accountStatus === 'pending' || accountStatus === 'suspended') {
    return <Navigate to="/auth/pending-approval" replace />;
  }

  // Category gating
  if (allowedCategories.length > 0 && !allowedCategories.includes(userCategory)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Roles gating (legacy + array)
  if (allowedRoles.length > 0) {
    const hasRole = allowedRoles.includes(userRole) || allowedRoles.some(r => rolesArray?.includes(r));
    if (!hasRole) return <Navigate to="/unauthorized" replace />;
  }

  // Page gating via allowedPages slugs
  if (allowedPages.length > 0) {
    const pages = new Set(userAllowedPages || []);
    const canAccessAny = pages.has('*') || allowedPages.some(p => pages.has(p));
    if (!canAccessAny) return <Navigate to="/unauthorized" replace />;
  }

  // If all checks pass, render the protected component
  return element;
};

export default ProtectedRoute;
